import NiceModal from '@ebay/nice-modal-react';
import { IS_PRODUCTION } from '@repo/common-utils';
import { usePageNavigationEvent, useUTM, useWorkingPlatform } from '@repo/platform';
import { EventTrackingProvider } from '@repo/tracking-service';
import { DialogProvider } from '@repo/ui';
import { AppSignProvider, AuthProvider, SignPaths } from '@repo/user-session';
import { useIntegrateAuthEventTracking } from '@repo/user-sign';
import { ToastProvider } from '@zenterprise-inc/ui';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import RelayEdkForAccessTokenProvider from '@/components/common/RelayEdkForAccessTokenProvider';
import Spinner from '@/components/common/Spinner';
import BasicFallback from '@/components/error/BasicFallback';
import { AUTH_PATHS, COMMON_PATHS } from '@/lib/constants/paths';
import { DATADOG_CONFIG, gtagGoogleAdsId, gtagGoogleAnalyticsId, gtmId } from '@/lib/constants/tracking';
import { SERVICE_APP_ID } from '@/lib/constants/variables';
import { useHometaxBlockSettings } from '@/lib/hooks/api/query/use-hometax-block.ts';
import { AppPropsWithLayout } from '@/lib/types/layout';
import { useRemoteConfig } from '@/lib/web/core/use-firebase';
import packageJSON from '@/package.json';

export const RefundAppContents = ({ Component, pageProps, ...rest }: AppPropsWithLayout) => {
  const { workingPlatform } = useWorkingPlatform();
  const { utmData } = useUTM();
  const navigationEvent = usePageNavigationEvent();
  const clientId = pageProps.clientId ?? `${SERVICE_APP_ID}-${workingPlatform.toLowerCase()}`;
  const isPersonalTritx = typeof window !== 'undefined' && sessionStorage.getItem('isPersonalTritx') === 'true';
  const landingId = isPersonalTritx ? 'capital-gain' : undefined;
  const sessionReplayPrecent = useRemoteConfig('SESSION_REPLAY_PRECENT')?.refund?.[IS_PRODUCTION ? 'prd' : 'dev'];
  const signPaths: SignPaths = {
    signIn: AUTH_PATHS.로그인,
    signUpInput: AUTH_PATHS.회원가입입력,
    signUpTerms: AUTH_PATHS.회원가입약관동의,
    signOut: AUTH_PATHS.로그아웃,
    ciCollectionRequest: AUTH_PATHS.CI수집요청,
    ciAuthentication: AUTH_PATHS.CI인증처리,
    forgotPassword: AUTH_PATHS.패스워드찾기,
    resetPassword: AUTH_PATHS.패스워드재설정,
    defaultReturn: COMMON_PATHS.환급조회목록
  };

  return (
    <ErrorBoundary FallbackComponent={BasicFallback}>
      <AuthProvider appId={SERVICE_APP_ID} clientId={clientId} signPaths={signPaths} navigationEvent={navigationEvent} isUseCI={workingPlatform !== 'toss'}>
        {utmData && sessionReplayPrecent && (
          <EventTrackingProvider
            appId={SERVICE_APP_ID}
            clientId={clientId}
            landingId={landingId}
            mixpanelProps={{
              mixpanelKey: process.env.NEXT_PUBLIC_MIXPANEL_KEY,
              mixpanelConfig: {
                record_sessions_percent: sessionReplayPrecent?.[utmData?.utmMedium ?? ''] ?? 0,
                record_mask_all_text: false,
                record_mask_all_inputs: false
              }
            }}
            airbridgeProps={{
              airbridgeAppId: process.env.NEXT_PUBLIC_ZENV === 'prd' ? 'bznav' : 'coredev',
              airbridgeWebToken: process.env.NEXT_PUBLIC_AIRBRIDGE_TOKEN
            }}
            metaPixelProps={{
              metaPixelId: process.env.NEXT_PUBLIC_META_PIXEL_ID
            }}
            tiktokPixelProps={{
              tiktokPixelId: 'CU8VUKBC77U44H5GAAG0'
            }}
            naverPixelProps={{
              naverPixelId: process.env.NEXT_PUBLIC_NAVER_PIXEL_ID
            }}
            kakaoPixelProps={{
              kakaoPixelId: process.env.NEXT_PUBLIC_KAKAO_PIXEL_ID
            }}
            googleTrackingProps={{
              googleTrackingIds: [gtagGoogleAdsId, gtagGoogleAnalyticsId, gtmId],
              initIds: [gtagGoogleAdsId, gtagGoogleAnalyticsId]
            }}
            datadogProps={{
              datadogConfig: DATADOG_CONFIG,
              appVersion: packageJSON.version
            }}
            navigationEvent={navigationEvent}
          >
            <RelayEdkForAccessTokenProvider pageProps={pageProps}>
              <PageContents Component={Component} pageProps={pageProps} {...rest} />
            </RelayEdkForAccessTokenProvider>
          </EventTrackingProvider>
        )}
      </AuthProvider>
    </ErrorBoundary>
  );
};

const PageContents = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout || ((page) => page);
  useIntegrateAuthEventTracking();
  useHometaxBlockSettings(true);

  return (
    <DialogProvider>
      <ToastProvider>
        <NiceModal.Provider>
          <Suspense fallback={<Spinner />}>
            <AppSignProvider signInUrl={AUTH_PATHS.로그인}>{getLayout(<Component {...pageProps} />, pageProps)}</AppSignProvider>
          </Suspense>
        </NiceModal.Provider>
      </ToastProvider>
    </DialogProvider>
  );
};
